import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { menuLanguages } from '../i18n/languages';
import '../css/LanguageSelector.css';

/** Flag + name chips in a two-column grid, the chosen language first (options menu and Settings). */
export function LanguageGrid({ onSelect, compact }) {
    const { language, setLanguage, t } = useLanguage();
    const languages = menuLanguages(language);
    if (languages.length <= 1) return null;
    return (
        <div className="lang-grid" role="group" aria-label={t('nav.selectLanguage')}>
            {languages.map(lang => (
                <button
                    key={lang.code}
                    type="button"
                    className={`lang-chip${lang.code === language ? ' active' : ''}`}
                    aria-pressed={lang.code === language}
                    onClick={() => { setLanguage(lang.code); onSelect?.(); }}
                >
                    <span className="lang-flag" aria-hidden="true">{lang.flag}</span>
                    <span className="lang-chip-name">{compact ? (lang.short || lang.name) : lang.name}</span>
                </button>
            ))}
        </div>
    );
}

/** Desktop navbar: globe button with a dropdown list. */
function LanguageSelector() {
    const { language, setLanguage, t } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <div className="lang-selector" ref={ref}>
            <button
                className="lang-button"
                onClick={() => setOpen(!open)}
                title={t('nav.language')}
                aria-label={t('nav.selectLanguage')}
                aria-haspopup="true"
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={faGlobe} />
            </button>
            {open && (
                <div className="lang-dropdown">
                    {menuLanguages(language).map(lang => (
                        <button
                            key={lang.code}
                            className={`lang-dropdown-item${lang.code === language ? ' active' : ''}`}
                            onClick={() => { setLanguage(lang.code); setOpen(false); }}
                        >
                            <span className="lang-flag">{lang.flag}</span>
                            <span className="lang-name">{lang.name}</span>
                            {lang.code === language && <span className="lang-check">{'✓'}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default LanguageSelector;
